/**
 * OnboardingTutorial - First-time user onboarding experience
 * Shows on first login, can be skipped at any time
 * Can be re-triggered from help menu
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  Rocket,
  FolderPlus,
  Users,
  MessageCircle,
  Hand,
  CheckCircle,
  SkipForward,
  HardHat
} from 'lucide-react';
import { Button } from '../ui/button';

const ONBOARDING_STORAGE_KEY = 'babelmix_onboarding_completed';

// Check if onboarding should be shown
export const shouldShowOnboarding = () => {
  return !localStorage.getItem(ONBOARDING_STORAGE_KEY);
};

// Mark onboarding as completed
export const markOnboardingComplete = () => {
  localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
};

// Reset onboarding (for testing or re-showing)
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_STORAGE_KEY);
};

const OnboardingTutorial = ({ onComplete, currentUser }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const steps = [
    {
      id: 'welcome',
      icon: Rocket,
      color: 'emerald',
      title: t('onboarding.welcome.title', 'Willkommen bei Babelmix!'),
      subtitle: t('onboarding.welcome.subtitle', 'Ihre mehrsprachige Projekt-Kommunikationsplattform'),
      description: t('onboarding.welcome.description', 'Wir zeigen Ihnen die wichtigsten Funktionen in wenigen Schritten. Sie können dieses Tutorial jederzeit überspringen.'),
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop',
      tip: t('onboarding.welcome.tip', 'Diese Tour dauert etwa 1 Minute')
    },
    {
      id: 'project',
      icon: FolderPlus,
      color: 'blue',
      title: t('onboarding.project.title', 'Projekte erstellen'),
      subtitle: t('onboarding.project.subtitle', 'Organisieren Sie Ihre Projekte'),
      description: t('onboarding.project.description', 'Jedes Projekt bekommt einen eigenen Bereich. Alle Kommunikation, Dateien und Aufgaben sind innerhalb von Projekten organisiert.'),
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop',
      tip: t('onboarding.project.tip', 'Tipp: Verwenden Sie klare, aussagekräftige Projektnamen')
    },
    {
      id: 'team',
      icon: Users,
      color: 'purple',
      title: t('onboarding.team.title', 'Ihr Team einladen'),
      subtitle: t('onboarding.team.subtitle', 'Kollegen, Auftragnehmer und Partner'),
      description: t('onboarding.team.description', 'Fügen Sie Teammitglieder per Telefonnummer hinzu. Sie erhalten eine SMS-Einladung und können sofort mitarbeiten. Jeder nutzt seine bevorzugte Sprache!'),
      features: [
        t('onboarding.team.feature1', '📱 Per SMS einladen'),
        t('onboarding.team.feature2', '🌍 Automatische Übersetzung'),
        t('onboarding.team.feature3', '👥 Unbegrenzte Teammitglieder')
      ],
      tip: t('onboarding.team.tip', 'Teammitglieder sehen Nachrichten in ihrer eigenen Sprache')
    },
    {
      id: 'communicate',
      icon: MessageCircle,
      color: 'orange',
      title: t('onboarding.communicate.title', 'Mühelos kommunizieren'),
      subtitle: t('onboarding.communicate.subtitle', 'Text, Sprache, Fotos und Dateien'),
      description: t('onboarding.communicate.description', 'Senden Sie Nachrichten in Ihrer Sprache - sie werden automatisch für Teammitglieder übersetzt, die andere Sprachen sprechen. Nutzen Sie Sprachnachrichten, wenn Tippen unpraktisch ist.'),
      features: [
        t('onboarding.communicate.feature1', '💬 Automatisch übersetzte Nachrichten'),
        t('onboarding.communicate.feature2', '🎤 Sprachnachrichten mit Transkription'),
        t('onboarding.communicate.feature3', '📎 Dateien und Fotos teilen')
      ],
      tip: t('onboarding.communicate.tip', 'Sprachnachrichten werden automatisch in Text transkribiert')
    },
    {
      id: 'construction',
      icon: HardHat,
      color: 'yellow',
      title: t('onboarding.construction.title', 'Feld-Modus'),
      subtitle: t('onboarding.construction.subtitle', 'Optimiert für den Einsatz vor Ort'),
      description: t('onboarding.construction.description', 'Große Buttons für die Nutzung mit Handschuhen, schnelle Fotodokumentation, Mängelmeldung - alles funktioniert offline! Daten synchronisieren automatisch, wenn Sie wieder online sind.'),
      features: [
        t('onboarding.construction.feature1', '📸 Schnelle Fotodokumentation'),
        t('onboarding.construction.feature2', '⚠️ Mängel sofort melden'),
        t('onboarding.construction.feature3', '📴 Volle Offline-Unterstützung')
      ],
      tip: t('onboarding.construction.tip', 'Funktioniert auch ohne Internetverbindung')
    },
    {
      id: 'ready',
      icon: CheckCircle,
      color: 'emerald',
      title: t('onboarding.ready.title', 'Alles bereit!'),
      subtitle: t('onboarding.ready.subtitle', 'Starten Sie jetzt mit Babelmix'),
      description: t('onboarding.ready.description', 'Sie kennen jetzt das Wichtigste. Erstellen Sie Ihr erstes Projekt und laden Sie Ihr Team ein. Brauchen Sie später Hilfe? Klicken Sie einfach jederzeit auf "?".'),
      features: [
        t('onboarding.ready.feature1', '✅ Erstes Projekt erstellen'),
        t('onboarding.ready.feature2', '✅ Teammitglieder einladen'),
        t('onboarding.ready.feature3', '✅ Kommunikation starten')
      ],
      tip: t('onboarding.ready.tip', 'Der Hilfe-Button "?" ist immer verfügbar')
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    setIsExiting(true);
    setTimeout(() => {
      markOnboardingComplete();
      onComplete();
    }, 300);
  };

  const handleComplete = () => {
    setIsExiting(true);
    setTimeout(() => {
      markOnboardingComplete();
      onComplete();
    }, 300);
  };

  const colorClasses = {
    emerald: { bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-600' },
    blue: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-600' },
    purple: { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-600' },
    orange: { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-600' },
    yellow: { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-600' }
  };

  const colors = colorClasses[currentStepData.color] || colorClasses.emerald;
  const Icon = currentStepData.icon;

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        paddingBottom: 'max(16px, env(safe-area-inset-bottom))'
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} />
      
      {/* Modal */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-lg transition-transform duration-300 flex flex-col ${
          isExiting ? 'scale-95' : 'scale-100'
        }`}
        style={{
          maxHeight: 'calc(100vh - 32px - env(safe-area-inset-bottom, 0px))',
          maxHeight: 'calc(100dvh - 32px - env(safe-area-inset-bottom, 0px))'
        }}
      >
        {/* Skip Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 z-10 flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
        >
          <SkipForward className="w-4 h-4" />
          {t('onboarding.skip', 'Überspringen')}
        </button>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200 flex-shrink-0 rounded-t-2xl overflow-hidden">
          <div 
            className={`h-full ${colors.bg} transition-all duration-500`}
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Header with Icon */}
          <div className={`${colors.light} px-6 pt-8 pb-6`}>
            <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 text-center">
              {currentStepData.title}
            </h2>
            <p className={`text-center ${colors.text} font-medium mt-1`}>
              {currentStepData.subtitle}
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <p className="text-gray-600 text-center mb-4">
              {currentStepData.description}
            </p>

            {/* Tutorial Image */}
            {currentStepData.image && (
              <div className="bg-gray-900 rounded-lg overflow-hidden mb-4">
                <img 
                  src={currentStepData.image} 
                  alt={currentStepData.title}
                  className="w-full"
                  loading="lazy"
                />
              </div>
            )}

            {/* Features List */}
            {currentStepData.features && (
              <div className="space-y-2 mb-4">
                {currentStepData.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-700">
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Tip */}
            {currentStepData.tip && (
              <div className={`${colors.light} rounded-lg p-3 text-sm ${colors.text}`}>
                💡 {currentStepData.tip}
              </div>
            )}
          </div>
        </div>

        {/* Navigation - Fixed at Bottom */}
        <div 
          className="px-6 py-4 flex items-center justify-between border-t bg-white flex-shrink-0 rounded-b-2xl"
          style={{
            paddingBottom: 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 16px))'
          }}
        >
          {/* Step Indicator */}
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep 
                    ? `${colors.bg} w-6` 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            {!isFirstStep && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('onboarding.back', 'Zurück')}
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              className={`${colors.bg} hover:opacity-90`}
            >
              {isLastStep ? (
                <>
                  {t('onboarding.getStarted', 'Los geht\'s')}
                  <CheckCircle className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  {t('onboarding.next', 'Weiter')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial;
